import React, { useCallback, useEffect, useState } from 'react';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormLabel,
    Grid,
    TextareaAutosize,
    TextField,
    Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';
import { Autocomplete } from '@material-ui/lab';
import useFetchRefundReasons from 'src/hooks/useFetchRefundReasons';
import RetailPaymentService from 'src/services/retailPaymentService'
import { createAxiosInstance } from 'src/utils/axios';

const retailPaymentService = new RetailPaymentService(
    createAxiosInstance(process.env.REACT_APP_CASH_QORE_API)
);

const useStyles = makeStyles((theme) => ({
    dialogTitleConatiner: {
        padding: theme.spacing(2),
        backgroundColor: theme.palette.primary.main,
    },
    dialogTitleText: {
        color: theme.palette.getContrastText(theme.palette.primary.dark),
        fontSize: theme.typography.h4.fontSize,
    },
    cancelledReasonInput: {
        width: '100%',
    }
}));

function CancelPaymentDialog({ isDialogOpen, setIsDialogOpen, handleClose, retailPaymentId, transactionId, onSuccess
}) {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const [filteredReasons, setFilteredReasons] = useState([]);
    const { reasons } = useFetchRefundReasons();
    const { email } = useSelector((state) => state.account.user);

    const validationSchema = Yup.object().shape({
        cancelledReasonId: Yup.number()
                            .required('Cancellation reason is required')
                            .min(1, 'Cancellation reason must be selected'),
        cancelledNote: Yup.string()
                            .required('Cancellation note is required')
                            .min(2, 'Cancellation note must be at least 2 characters long'),
    });

    const onSubmit = useCallback(async (values) => {
        try {
            console.log("Cancelling payment with values: ", values);
            const respone = await retailPaymentService.cancelRetailPayment(values);
             if (respone?.data){
                formik.resetForm();
                enqueueSnackbar('Payment has been cancelled successfully.', {
                variant: 'success',
                });

                if (onSuccess) {
                    onSuccess();
                }
            }
            
        } catch (error) {
            enqueueSnackbar(`Issue cancelling payment. ${error.message}`, { variant: 'error' });
        }
        finally {
            setIsDialogOpen(false);
        }
    }, [retailPaymentId, transactionId]);

    const formik = useFormik({
        initialValues: {
            cancelledReasonId: -1,
            cancelledNote: '',
            userEmail: email,
            transactionId: isNaN(Number(transactionId)) ? null : Number(transactionId),
            retailPaymentId: isNaN(Number(retailPaymentId)) ? null : Number(retailPaymentId),
        },
        validationSchema: validationSchema,
        onSubmit,
    });

    useEffect(() => {
        if (reasons?.length > 0) {
            setFilteredReasons(reasons.filter((reason) => !reason.isInternal));
        }
    }, [reasons]);

    return (
        <Dialog
            open={isDialogOpen}
            onClose={(_, reason) => handleClose(reason)}
            fullwidth
            maxWidth="xs"
        >
            <DialogTitle className={classes.dialogTitleConatiner}>
                <Typography className={classes.dialogTitleText}>
                    Confirm Cancel Payment
                </Typography>
            </DialogTitle>
            <form onSubmit={formik.handleSubmit}>
                <DialogContent>
                    <Typography style={{ marginBottom: '20px' }}>
                       Enter reason and notes for cancellation.
                    </Typography>
                    <Grid container spacing={2} fullwidth>
                        <Grid item xs={12} spacing={2}>
                            <FormControl fullwidth className={classes.cancelledReasonInput} >
                                <Autocomplete
                                    fullwidth
                                    options={filteredReasons}
                                    onChange={(event, value) => {
                                        formik.setFieldValue('cancelledReasonId', value ? value?.refundReasonId : null);
                                    }}
                                    getOptionLabel={(option) => option.reason}
                                    renderInput={(params) => (
                                        <TextField {...params}
                                            label="Reason"
                                            variant="outlined"
                                            fullwidth
                                            name='cancelledReasonId'
                                            error={formik.touched.cancelledReasonId && Boolean(formik.errors.cancelledReasonId)}
                                            helperText={formik.touched.cancelledReasonId && formik.errors.cancelledReasonId}
                                        />
                                    )}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormLabel>Notes</FormLabel>
                            <TextareaAutosize
                                multiline="true"
                                minRows={6}
                                fullwidth
                                placeholder="Enter notes here"
                                name="cancelledNote"
                                value={formik.values.cancelledNote}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                style={{ width: '100%' }}
                                error={formik.touched.cancelledNote && Boolean(formik.errors.cancelledNote)}
                                helperText={formik.touched.cancelledNote && formik.errors.cancelledNote}
                            />
                            {formik.touched.cancelledNote && formik.errors.cancelledNote && (
                                <Typography color="error" variant="caption">
                                    {formik.errors.cancelledNote}
                                </Typography>
                            )}
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions
                    id="selected-records-actions"
                    data-test="selected-records-actions"
                    className={classes.dialogActions}
                >
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={() => {
                            setIsDialogOpen(false);
                            formik.resetForm();
                        }}
                        disabled={formik.isSubmitting}
                    >
                        Close
                    </Button>
                    <Button
                        color="primary"
                        variant="contained"
                        type="submit"
                        disabled={!(formik.isValid && formik.dirty) || formik.isSubmitting}
                    >
                        {formik.isSubmitting && <CircularProgress size={24} style={{ marginRight: '12px' }} />}
                        Yes, Cancel Payment
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}

CancelPaymentDialog.propTypes = {
    isDialogOpen: PropTypes.bool,
    setIsDialogOpen: PropTypes.func,
    handleClose: PropTypes.func,
    transactionId: PropTypes.number,
    onSuccess: PropTypes.func,
};

export default CancelPaymentDialog;
